import React from 'react';
import type { ClinicalSummary as Summary } from '../../../services/patientService';
import { formatDate } from '../../../lib/format';
import styles from './ClinicalSummary.module.css';

interface Props {
  summary: Summary | null;
  loading: boolean;
}

const formatTime = (value?: string | null): string =>
  value ? value.slice(0, 5) : '';

/**
 * The band above the patient tabs: allergies, latest vitals, recent
 * prescriptions and the next appointment.
 *
 * Two rules run through it. Sections that are empty say so rather than
 * disappearing — a missing allergies card is indistinguishable from a
 * patient with no allergies, and the difference matters. And every
 * reading carries its own date, because vitals may come from an earlier
 * visit and a prescription's duration is free text that cannot tell us
 * whether the patient is still taking it.
 */
const ClinicalSummary: React.FC<Props> = ({ summary, loading }) => {
  if (loading || !summary) return null;

  const { allergies, latest_vitals, recent_medications, last_consultation, next_appointment } = summary;
  const hasAllergies = allergies.length > 0;

  return (
    <div className={styles.band} data-testid="clinical-summary">
      {/* ── Allergies ─────────────────────────────────────── */}
      <section className={`${styles.card} ${hasAllergies ? styles.cardAlert : ''}`}>
        <h3 className={styles.head}>
          <i className="fa fa-triangle-exclamation" aria-hidden="true" />
          Alergias
        </h3>

        {hasAllergies ? (
          <ul className={styles.allergyList}>
            {allergies.map(a => (
              <li key={a.id}>
                <span className={styles.allergyName}>{a.name}</span>
                {a.category && <span className={styles.allergyNote}>{a.category}</span>}
                {a.notes && <span className={styles.allergyNote}>{a.notes}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>Sin alergias registradas</p>
        )}
      </section>

      {/* ── Latest vitals ─────────────────────────────────── */}
      <section className={styles.card}>
        <h3 className={styles.head}>
          <i className="fa fa-heart-pulse" aria-hidden="true" />
          Signos vitales
        </h3>

        {latest_vitals ? (
          <>
            <div className={styles.vitalGrid}>
              {latest_vitals.blood_pressure && (
                <div>
                  <span className={styles.vitalLabel}>Presión</span>
                  <span className={styles.vitalValue}>{latest_vitals.blood_pressure}</span>
                  <span className={styles.vitalUnit}>mmHg</span>
                </div>
              )}
              {latest_vitals.heart_rate != null && (
                <div>
                  <span className={styles.vitalLabel}>Pulso</span>
                  <span className={styles.vitalValue}>{latest_vitals.heart_rate}</span>
                  <span className={styles.vitalUnit}>lpm</span>
                </div>
              )}
              {latest_vitals.temperature && (
                <div>
                  <span className={styles.vitalLabel}>Temp.</span>
                  <span className={styles.vitalValue}>{latest_vitals.temperature}</span>
                  <span className={styles.vitalUnit}>°C</span>
                </div>
              )}
              {latest_vitals.oxygen_saturation != null && (
                <div>
                  <span className={styles.vitalLabel}>SpO₂</span>
                  <span className={styles.vitalValue}>{latest_vitals.oxygen_saturation}</span>
                  <span className={styles.vitalUnit}>%</span>
                </div>
              )}
              {latest_vitals.weight && (
                <div>
                  <span className={styles.vitalLabel}>Peso</span>
                  <span className={styles.vitalValue}>{latest_vitals.weight}</span>
                  <span className={styles.vitalUnit}>kg</span>
                </div>
              )}
              {latest_vitals.bmi && (
                <div>
                  <span className={styles.vitalLabel}>IMC</span>
                  <span className={styles.vitalValue}>{latest_vitals.bmi}</span>
                </div>
              )}
            </div>
            <p className={styles.footNote}>
              Tomados el {formatDate(latest_vitals.recorded_at)}
            </p>
          </>
        ) : (
          <p className={styles.empty}>Sin signos vitales registrados</p>
        )}
      </section>

      {/* ── Prescriptions ─────────────────────────────────── */}
      <section className={styles.card}>
        <h3 className={styles.head}>
          <i className="fa fa-pills" aria-hidden="true" />
          Medicación recetada
        </h3>

        {recent_medications.length > 0 ? (
          <ul className={styles.itemList}>
            {recent_medications.slice(0, 4).map((m, i) => (
              <li key={`${m.consultation_id}-${i}`}>
                <span className={styles.itemName}>{m.medication_name}</span>
                {(m.dosage || m.frequency) && (
                  <span className={styles.itemDetail}>
                    {[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}
                  </span>
                )}
                <span className={styles.stamp}>Recetada el {formatDate(m.prescribed_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>Sin recetas registradas</p>
        )}
      </section>

      {/* ── Next appointment / last visit ─────────────────── */}
      <section className={styles.card}>
        <h3 className={styles.head}>
          <i className="fa fa-calendar-check" aria-hidden="true" />
          Próxima cita
        </h3>

        {next_appointment ? (
          <div>
            <span className={styles.itemName}>
              {[formatDate(next_appointment.date), formatTime(next_appointment.time)]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {next_appointment.doctor && (
              <span className={styles.itemDetail}>{next_appointment.doctor}</span>
            )}
            {next_appointment.reason && (
              <span className={styles.stamp}>{next_appointment.reason}</span>
            )}
          </div>
        ) : (
          <p className={styles.empty}>Sin citas programadas</p>
        )}

        {last_consultation && (
          <p className={styles.footNote}>
            Última consulta: {formatDate(last_consultation.date)}
            {last_consultation.doctor && ` · ${last_consultation.doctor}`}
          </p>
        )}
      </section>
    </div>
  );
};

export default ClinicalSummary;
